import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Globe, Wrench, Clock, Mail, BookOpen, ArrowRight, Home } from 'lucide-react';
import { Button, LanguageModal } from '@/components/ui';

export type MaintenanceType = 'app' | 'marketing' | 'full';

interface MaintenancePageProps {
  type?: MaintenanceType;
  estimatedMinutes?: number;
}

const content = {
  hr: {
    app: {
      title: 'Aplikacija se ažurira',
      subtitle: 'Vraćamo se uskoro',
      description:
        'BookiApp aplikacija je trenutno u održavanju. Marketing stranica i blog rade normalno.',
      stayLink: 'Pročitaj naš blog',
      stayDescription: 'Vodiči i savjeti za iznajmljivače',
    },
    marketing: {
      title: 'Web stranica se ažurira',
      subtitle: 'Vraćamo se uskoro',
      description:
        'Naša marketing stranica je trenutno u održavanju. Aplikacija za postojeće korisnike radi normalno.',
      stayLink: 'Otvori aplikaciju',
      stayDescription: 'Dashboard za iznajmljivače i guest portal',
    },
    full: {
      title: 'Održavanje u tijeku',
      subtitle: 'Radimo na poboljšanjima',
      description:
        'BookiApp je trenutno nedostupan zbog planiranog održavanja. Vraćamo se uskoro s novim značajkama i poboljšanjima.',
      stayLink: null,
      stayDescription: null,
    },
    estimatedTime: 'Očekivano trajanje:',
    minutes: 'minuta',
    contact: 'Za hitna pitanja, kontaktirajte nas:',
    email: 'admin@bookiapp.com',
    thankYou: 'Hvala na strpljenju!',
    seoTitle: 'BookiApp — Održavanje u tijeku',
    seoDescription: 'BookiApp je trenutno u održavanju. Vraćamo se uskoro.',
  },
  en: {
    app: {
      title: 'App is updating',
      subtitle: 'Back online shortly',
      description:
        'The BookiApp application is currently under maintenance. Our marketing site and blog remain available.',
      stayLink: 'Read our blog',
      stayDescription: 'Guides and tips for vacation rental hosts',
    },
    marketing: {
      title: 'Website is updating',
      subtitle: 'Back online shortly',
      description:
        'Our marketing site is currently under maintenance. The application for existing users works normally.',
      stayLink: 'Open the app',
      stayDescription: 'Host dashboard and guest portal',
    },
    full: {
      title: 'Maintenance in Progress',
      subtitle: "We're working on improvements",
      description:
        "BookiApp is currently unavailable due to scheduled maintenance. We'll be back soon with new features and improvements.",
      stayLink: null,
      stayDescription: null,
    },
    estimatedTime: 'Estimated duration:',
    minutes: 'minutes',
    contact: 'For urgent questions, contact us:',
    email: 'admin@bookiapp.com',
    thankYou: 'Thank you for your patience!',
    seoTitle: 'BookiApp — Maintenance in Progress',
    seoDescription: 'BookiApp is currently under maintenance. Back online shortly.',
  },
};

export function MaintenancePage({
  type = 'full',
  estimatedMinutes = 30,
}: MaintenancePageProps) {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];
  const variant = t[type];

  const [langOpen, setLangOpen] = useState(false);

  // Different gradients per variant — keeps the page visually distinct
  const gradients: Record<MaintenanceType, { bg: string; icon: string; accent: string }> = {
    app: {
      bg: 'from-amber-500/20 to-orange-500/20',
      icon: 'from-amber-500 to-orange-600',
      accent: 'text-amber-600 dark:text-amber-400',
    },
    marketing: {
      bg: 'from-violet-500/20 to-purple-500/20',
      icon: 'from-violet-500 to-purple-600',
      accent: 'text-violet-600 dark:text-violet-400',
    },
    full: {
      bg: 'from-rose-500/20 to-orange-500/20',
      icon: 'from-rose-500 to-orange-600',
      accent: 'text-rose-600 dark:text-rose-400',
    },
  };
  const g = gradients[type];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
      <Helmet>
        <title>{t.seoTitle}</title>
        <meta name="description" content={t.seoDescription} />
        {/* During maintenance we want Google to retry, NOT to deindex.
            The 503 status + Retry-After is set via public/_headers (Cloudflare
            Pages); here we simply stay indexable so blog/marketing rank when
            we come back. */}
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-[hsl(var(--border))]/50">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLangOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={langOpen}
          >
            <Globe className="h-4 w-4 mr-2" />
            {i18n.language.toUpperCase()}
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          {/* Animated icon */}
          <div className="relative mx-auto w-24 h-24 mb-8">
            <div
              className={`absolute inset-0 bg-gradient-to-br ${g.bg} rounded-full animate-pulse`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className={`w-20 h-20 rounded-full bg-gradient-to-br ${g.icon} flex items-center justify-center animate-spin-slow shadow-lg`}
              >
                <Wrench className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{variant.title}</h1>
          <p className={`text-lg ${g.accent} font-medium mb-4`}>{variant.subtitle}</p>

          {/* Description */}
          <p className="text-[hsl(var(--muted-foreground))] mb-8">{variant.description}</p>

          {/* Estimated time */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${g.bg} border border-[hsl(var(--border))]/50 mb-8`}
          >
            <Clock className={`h-4 w-4 ${g.accent}`} />
            <span className={`text-sm ${g.accent}`}>
              {t.estimatedTime} ~{estimatedMinutes} {t.minutes}
            </span>
          </div>

          {/* "Stay on site" CTA — directs traffic to the part that's still up.
              Skipped entirely on full maintenance to avoid dead links. */}
          {variant.stayLink && type === 'app' && (
            <Link
              to="/hr/blog/"
              className="block mb-6 group p-5 rounded-2xl border border-[hsl(var(--border))]/60 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 hover:border-cyan-500/40 hover:shadow-lg hover:shadow-cyan-500/10 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                  <BookOpen className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold">{variant.stayLink}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {variant.stayDescription}
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-cyan-600 dark:text-cyan-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          )}
          {variant.stayLink && type === 'marketing' && (
            <Link
              to="/sign-in"
              className="block mb-6 group p-5 rounded-2xl border border-[hsl(var(--border))]/60 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 hover:border-cyan-500/40 hover:shadow-lg hover:shadow-cyan-500/10 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                  <Home className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold">{variant.stayLink}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {variant.stayDescription}
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-cyan-600 dark:text-cyan-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          )}

          {/* Contact */}
          <div className="bg-[hsl(var(--muted))] rounded-xl p-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{t.contact}</p>
            <a
              href={`mailto:${t.email}`}
              className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
            >
              <Mail className="h-4 w-4" />
              {t.email}
            </a>
          </div>

          <p className="mt-8 text-lg font-medium">{t.thankYou}</p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[hsl(var(--border))]/50 py-4">
        <div className="container mx-auto px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          &copy; 2026 BookiApp. All rights reserved.
        </div>
      </footer>

      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-spin-slow, .animate-pulse { animation: none !important; }
        }
      `}</style>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </div>
  );
}

export default MaintenancePage;
